import type { ICascaderMenuOption, TStringPath } from './types';

/**
 * 在树形 options 中按路径查找对应的节点链路。
 *
 * 为什么返回 `null`：
 * - 面板支持“搜索过滤后剪枝”的数据视图；以及业务侧异步回填 `data` 时短暂的不一致。
 * - 当 `value` 存在历史路径或后端下发数据变更导致路径失效时，直接返回 `null` 让上层决定如何降级（忽略该路径/回退展示），避免抛错导致整个面板不可用。
 */
export const findPathOptions = (
  options: ICascaderMenuOption[],
  path: TStringPath,
): ICascaderMenuOption[] | null => {
  const result: ICascaderMenuOption[] = [];
  let currentLevel = options;

  for (const pathValue of path) {
    const node = currentLevel.find((item) => item.value === pathValue);
    if (!node) {
      return null;
    }
    result.push(node);
    currentLevel = node.children || [];
  }

  return result;
};

/**
 * 将选中的路径数组映射为“节点链路数组”，用于回显与自定义渲染。
 *
 * 约束：
 * - 仅返回在当前 `data` 中可解析的链路；无法解析的路径会被忽略。
 * - 这使得组件在“数据源变化 / 搜索剪枝 / 旧值回放”的场景下更稳健：回显尽力而为，但不制造错误状态。
 */
export const getSelectedOptions = (
  data: ICascaderMenuOption[],
  value: TStringPath[],
): ICascaderMenuOption[][] => {
  if (!Array.isArray(value) || value.length === 0) {
    return [];
  }

  const result: ICascaderMenuOption[][] = [];

  value.forEach((path) => {
    const chain = findPathOptions(data, path);
    if (chain) {
      result.push(chain);
    }
  });

  return result;
};

/**
 * 默认的触发区回显策略：取每条链路的末级 `label`，以逗号拼接。
 *
 * 约束与取舍：
 * - 只对 string/number 做可预期的字符串化；复杂 ReactNode 不做文本提取，避免出现 “[object Object]” 或不可控的序列化结果。
 * - 回显仅用于展示摘要，完整信息可通过 `title` 或自定义 `valueRender` 承载。
 */
export const getDefaultDisplayText = (
  selectedChains: ICascaderMenuOption[][],
): string => {
  if (!selectedChains.length) {
    return '';
  }
  const labels: string[] = selectedChains.map((chain) => {
    const last = chain[chain.length - 1];
    if (!last) return '';
    if (typeof last.label === 'string') {
      return last.label;
    }
    if (typeof last.label === 'number') {
      return String(last.label);
    }
    return '';
  });

  return labels.filter(Boolean).join(', ');
};
